// Hooks for the Agenda tab.
import { useState, useRef, useEffect } from "react";
import { LOAD_ERROR_MESSAGE } from "./agenda.constants";
import { POLL_INTERVAL_MS } from "../insights.constants";
import { useInsightPolling, useAddedHighlight } from "../insights.hooks";
import { scrollToBottom } from "../insights.utils";
import {
  fetchAgendaItems,
  addAgendaItem,
  updateAgendaItem,
  deleteAgendaItem,
  fromBackend,
  makeAgendaItem,
} from "./agenda.utils";
import { isLocalhost } from "../../../utils/environmentUtils";
import LogManager from "../../../utils/logManager";

/**
 * useAgendaItems - Agenda-specific configuration of the shared useInsightPolling hook.
 *
 * Fetches from /agenda on mount and falls back to polling only while the WebSocket is
 * disconnected. `requestExtras` adds the endpoint's required `operation`, and `mapItems`
 * normalizes the backend shape (itemId/content/status) to the frontend one (id/title/state)
 * before rows reach the store. Skips the initial fetch when Home's badge seed already ran.
 *
 * Deliberately does NOT set `useSince` (Action Items does): agenda is not append-only — existing
 * rows change status as the meeting progresses, so each poll needs the full list.
 *
 * Order and deletions are both reconciled now: the store re-sorts with compareAgendaItems on
 * every write, and `pruneMissing` drops rows a full snapshot no longer contains.
 */
export const useAgendaItems = (meetingUuid, insightStore) =>
  useInsightPolling({
    meetingUuid,
    insightStore,
    endpoint: "/agenda",
    pollIntervalMs: POLL_INTERVAL_MS,
    storeKey: "agendaItems",
    responseKey: "agendaItems",
    upsertKey: "upsertAgendaItems",
    errorLabel: "Failed to fetch agenda items:",
    pollErrorLabel: "Fallback poll for agenda items failed:",
    errorMessage: LOAD_ERROR_MESSAGE,
    initialIsLoading: true,
    initialHasFetched: false,
    requireNonEmpty: true,
    skipInitialFetchIfSeeded: true,
    requestExtras: { operation: "query" },
    mapItems: fromBackend,
    // The response is a full snapshot, so absent rows mean deleted — prune them.
    upsertOptions: { pruneMissing: true },
  });

/**
 * useAgendaSeed - Seeds insightStore with existing agenda items as soon as the session is
 * active, so the tab's count pill shows immediately without waiting for the tab to be clicked.
 * Mirrors useActionItemsSeed; Home calls it so the badge seeds without opening the tab, while
 * the Agenda component's own useAgendaItems handles the ongoing fetch/poll (and skips its
 * initial fetch when this seed already populated the store).
 */
export const useAgendaSeed = ({
  meetingUuid,
  insightStore,
  isSessionActive,
  isTranscriptOnly,
  isEnabled,
}) => {
  // Seed at most once per Home mount — prevents re-fetching if the list later empties, and lets
  // the seed bow out when the WebSocket/poll populates the store first.
  const hasSeededRef = useRef(false);

  useEffect(() => {
    if (!isEnabled) return undefined;
    // Localhost has no /agenda backend — skip so local dev doesn't 404 (Home seeds mock data).
    if (isLocalhost()) return undefined;
    if (!meetingUuid) return undefined;
    if (!isSessionActive) return undefined;
    // Transcript-only sessions also reach SUMMARIZING but /agenda isn't served for them.
    if (isTranscriptOnly) return undefined;
    if (hasSeededRef.current) return undefined;

    let stale = false;
    const seed = async () => {
      try {
        const agendaItems = await fetchAgendaItems(meetingUuid);
        // Bail if superseded (unmount, meetingUuid/session change) so a late response can't
        // clobber fresher state.
        if (stale) return;
        if (agendaItems.length > 0) {
          insightStore.upsertAgendaItems(agendaItems);
          hasSeededRef.current = true;
        }
      } catch (err) {
        if (!stale) LogManager.error("Failed to seed initial agenda items:", err);
      }
    };

    seed();
    return () => {
      stale = true;
    };
    // Not keyed on insightStore.agendaItems.length: this is a seed-once effect (guarded by
    // hasSeededRef), not one that should re-fire whenever the list changes.
  }, [meetingUuid, isSessionActive, isTranscriptOnly, isEnabled]);
};

/**
 * useAgendaActions - Add/edit/delete UI state and handlers, with optimistic writes through
 * insightStore. Add/edit input values live here since each row is a single title field.
 * Edit and add rows stay open until their own save/cancel, so several can be open at once; the
 * delete confirmation is single-row and any other action cancels it.
 */
export const useAgendaActions = ({ meetingUuid, insightStore, scrollRef }) => {
  const [editingIds, setEditingIds] = useState(() => new Set());
  const [editValues, setEditValues] = useState({});
  const [isAdding, setIsAdding] = useState(false);
  const [addValue, setAddValue] = useState("");
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const prevTitles = useRef({});
  const { highlightId, flash } = useAddedHighlight();

  const setEditValue = (itemId, value) => setEditValues((prev) => ({ ...prev, [itemId]: value }));

  // Drops every piece of a row's edit state; shared by save and cancel.
  const closeEdit = (itemId) => {
    setEditingIds((prev) => {
      const next = new Set(prev);
      next.delete(itemId);
      return next;
    });
    setEditValues((prev) => {
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
    delete prevTitles.current[itemId];
  };

  const startEdit = (item) => {
    setPendingDeleteId(null); // another action cancels an open delete confirmation
    setEditingIds((prev) => new Set(prev).add(item.id));
    setEditValues((prev) => ({ ...prev, [item.id]: item.title }));
    prevTitles.current[item.id] = item.title;
  };

  const saveEdit = (itemId) => {
    const title = (editValues[itemId] || "").trim();
    const prevTitle = prevTitles.current[itemId];
    setPendingDeleteId(null);

    if (title) {
      insightStore.upsertAgendaItems([{ id: itemId, title }], { force: true });

      if (!isLocalhost()) {
        updateAgendaItem(meetingUuid, itemId, title)
          .then((updated) => {
            if (!updated) {
              insightStore.upsertAgendaItems([{ id: itemId, title: prevTitle }], { force: true });
            }
          })
          .catch((err) => {
            LogManager.error("Failed to update agenda item, rolling back", err);
            insightStore.upsertAgendaItems([{ id: itemId, title: prevTitle }], { force: true });
          });
      }
    }
    closeEdit(itemId);
  };

  const cancelEdit = (itemId) => {
    setPendingDeleteId(null);
    closeEdit(itemId);
  };

  const startAdd = () => {
    setPendingDeleteId(null);
    setIsAdding(true);
  };

  const saveAdd = () => {
    const title = addValue.trim();
    if (title) {
      setPendingDeleteId(null);
      const id = `agenda#${crypto.randomUUID()}`;
      insightStore.upsertAgendaItems([makeAgendaItem({ id, title })]);
      flash(id);
      scrollToBottom(scrollRef);

      if (!isLocalhost()) {
        addAgendaItem(meetingUuid, id, title)
          .then((created) => {
            if (!created) {
              LogManager.warn("Add returned CONFLICT — rolling back");
              insightStore.removeAgendaItem(id);
              return;
            }
            // Apply the server's row: it carries the detectedAt that makes this item versionable,
            // and only a versioned item passes the agenda_status_changed gate.
            insightStore.upsertAgendaItems([created]);
          })
          .catch((err) => {
            LogManager.error("Failed to add agenda item, rolling back", err);
            insightStore.removeAgendaItem(id);
          });
      }
    }
    setAddValue("");
    setIsAdding(false);
  };

  const cancelAdd = () => {
    setPendingDeleteId(null);
    setAddValue("");
    setIsAdding(false);
  };

  // Delete confirmation — transient and single-row. Starting it on another row (or the delete
  // itself) replaces/clears the current one; opening an edit/add also clears it (see above).
  const startDelete = (item) => setPendingDeleteId(item.id);
  const confirmDelete = (item) => {
    insightStore.removeAgendaItem(item.id);
    setPendingDeleteId(null);

    if (!isLocalhost()) {
      deleteAgendaItem(meetingUuid, item.id).catch((err) => {
        LogManager.error("Failed to delete agenda item, rolling back", err);
        // Append to end — recomputing original position is not reliable after concurrent
        // mutations, and append is safe (the next poll will reorder if needed).
        insightStore.upsertAgendaItems([item], { force: true });
      });
    }
  };
  const cancelDelete = () => setPendingDeleteId(null);

  return {
    add: { open: isAdding, start: startAdd, save: saveAdd, cancel: cancelAdd, value: addValue, setValue: setAddValue },
    edit: { ids: editingIds, start: startEdit, save: saveEdit, cancel: cancelEdit, values: editValues, setValue: setEditValue },
    del: { pendingId: pendingDeleteId, start: startDelete, confirm: confirmDelete, cancel: cancelDelete },
    highlightId,
  };
};
